import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  // Load user profile with caching
  const loadUserProfile = async (authUser) => {
    try {
      const cachedProfile = sessionStorage.getItem(`ecom_profile_${authUser.id}`);
      if (cachedProfile) return JSON.parse(cachedProfile);

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (error || !profile) {
        return {
          id: authUser.id,
          email: authUser.email,
          name: authUser.email?.split('@')[0] || 'User',
          role: 'customer'
        };
      }

      const userProfile = {
        id: authUser.id,
        email: profile.email,
        name: profile.name,
        role: profile.role,
        phone: profile.phone,
        customer_id: profile.customer_id,
        promoter_id: profile.promoter_id,
      };

      sessionStorage.setItem(`ecom_profile_${authUser.id}`, JSON.stringify(userProfile));
      return userProfile;
    } catch (error) {
      return {
        id: authUser.id,
        email: authUser.email,
        name: authUser.email?.split('@')[0] || 'User',
        role: 'customer'
      };
    }
  };

  useEffect(() => {
    const getInitialSession = async () => {
      try {
        setLoading(true);

        // Check local auth state first (Customer/Promoter)
        if (authService.isAuthenticated()) {
          const localUser = authService.getCurrentUser();
          const localSession = authService.getCurrentSession();
          if (localUser && localSession) {
            setUser(localUser);
            setSession(localSession);
            setLoading(false);
            return;
          }
        }

        // Check Supabase session
        const { data: { session: supabaseSession } } = await supabase.auth.getSession();
        if (supabaseSession?.user) {
          setSession(supabaseSession);
          const profile = await loadUserProfile(supabaseSession.user);
          setUser(profile);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };
    getInitialSession();
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(async () => {
        if (event === 'SIGNED_IN' && session?.user) {
          setSession(session);
          const profile = await loadUserProfile(session.user);
          setUser(profile);
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          sessionStorage.clear();
        }
        setLoading(false);
      }, 0);
    });
    return () => subscription.unsubscribe();
  }, []);

  const login = async (identifier, password, role = null) => {
    try {
      let userProfile;

      // Only customers are allowed to login to this ecom portal
      if (role !== 'customer') {
        throw new Error('Only customers are allowed to access BrightBazaar. Promoters and admins should use the admin portal.');
      }

      userProfile = await authService.loginWithCardNo(identifier, password);

      // Double-check the role from the profile
      if (userProfile.role && userProfile.role !== 'customer') {
        authService.logout();
        throw new Error('Only customers are allowed to access BrightBazaar. Please use the admin portal.');
      }

      setUser(userProfile);
      setSession(authService.getCurrentSession());

      return userProfile;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      sessionStorage.clear();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, session, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
